import React from 'react';
import { format } from 'date-fns';

interface Workflow {
  id: string;
  customer_id: string;
  spreadsheet_url?: string;
  status: string;
  current_step: string;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  total_accounts: number;
  processed_accounts: number;
  total_work_units: number;
  completed_work_units: number;
}

interface RecentWorkflowsProps {
  workflows?: Workflow[];
}

const RecentWorkflows: React.FC<RecentWorkflowsProps> = ({ workflows = [] }) => {
  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, string> = {
      pending: 'bg-zinc-800 text-zinc-400 border border-zinc-700',
      processing: 'bg-blue-500/20 text-blue-400 border border-blue-500/30',
      completed: 'bg-green-500/20 text-green-400 border border-green-500/30',
      failed: 'bg-red-500/20 text-red-400 border border-red-500/30',
      cancelled: 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30',
    };

    return statusColors[status.toLowerCase()] || 'bg-zinc-800 text-zinc-400 border border-zinc-700';
  };

  const formatStepName = (step: string) => {
    if (!step) return 'Pending';
    // Convert snake_case or CONSTANT_CASE to Title Case
    return step
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Sort workflows by updated_at (most recent first) and take the first 5
  const displayWorkflows = workflows
    .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
    .slice(0, 5);

  return (
    <div className="dashboard-card">
      <h2 className="text-xl font-semibold mb-4 text-white">Recent Workflows</h2>
      
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className="table-header">
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">
                Workflow ID
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">
                Customer
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">
                Current Step
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">
                Progress
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">
                Updated
              </th>
            </tr>
          </thead>
          <tbody>
            {displayWorkflows.length > 0 ? (
              displayWorkflows.map((workflow) => (
                <tr key={workflow.id} className="table-row">
                  <td className="px-4 py-3 text-sm font-medium text-white font-mono">
                    {workflow.id.substring(0, 8)}...
                  </td>
                  <td className="px-4 py-3 text-sm text-zinc-400">
                    {workflow.customer_id}
                  </td>
                  <td className="px-4 py-3 text-sm text-zinc-400">
                    {formatStepName(workflow.current_step)}
                  </td>
                  <td className="px-4 py-3 text-sm text-zinc-400">
                    {workflow.total_accounts > 0 ? (
                      <div className="flex items-center gap-2">
                        <span>{workflow.processed_accounts}/{workflow.total_accounts}</span>
                        <div className="w-16 bg-zinc-800 rounded-full h-1.5">
                          <div
                            className="bg-gradient-to-r from-orange-500 to-yellow-500 h-1.5 rounded-full transition-all"
                            style={{
                              width: `${(workflow.processed_accounts / workflow.total_accounts) * 100}%`
                            }}
                          />
                        </div>
                      </div>
                    ) : (
                      <span className="text-zinc-600">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(workflow.status)}`}>
                      {workflow.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-zinc-400">
                    {format(new Date(workflow.updated_at), 'MMM d, HH:mm')}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-zinc-500">
                  No workflows yet. Create your first workflow to get started.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RecentWorkflows;